import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './pages/login/login.component';
import { HomeComponent } from './pages/home/home.component';
import { NoAutorizadoComponent } from './pages/shared/no-autorizado/no-autorizado.component';
import { MantenimientoComponent } from './pages/shared/mantenimiento/mantenimiento.component';

import { ProjectControlModule } from './pages/project-control/project-control.module';
import { UpdateContactInfoComponent } from './pages/public/update-contact-info/update-contact-info.component';

import { RecuperarComponent } from './pages/recuperar/recuperar.component';
import { AuthGuard } from './guards/auth.guard';
import { PermisosGuard } from './guards/permisos.guard';
import { PublicProspectoFormComponent } from './pages/recursos-humanos/contratacion/prospectos-management/public-forms/public-prospecto-form.component';
import { NotFoundComponent } from './pages/shared/not-found/not-found.component';

const routes: Routes = [
  { path: 'credenciales', title: 'ZIVO ERP | Credenciales', canActivate: [AuthGuard],
    loadComponent: () => import('./pages/credenciales/credenciales.component').then(m => m.CredencialesComponent) },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginComponent, title: 'ZIVO ERP | Iniciar sesión' },
  { path: 'recuperar', component: RecuperarComponent, title: 'ZIVO ERP | Recuperar contraseña' },
  { path: 'home', component: HomeComponent, canActivate: [AuthGuard], title: 'ZIVO ERP | Inicio', },
  {
    path: 'mis-asistencias', title: 'ZIVO ERP | Mis asistencias', canActivate: [AuthGuard],
    loadComponent: () => import('./pages/recursos-humanos/attendance/self-attendance.component').then(m => m.SelfAttendanceComponent),
  },
  {
    path: 'mi-biometrico',
    title: 'ZIVO ERP | Mi registro biométrico',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import(
        './pages/recursos-humanos/personal/zkbio-self-enrollment/zkbio-self-enrollment.component'
      ).then((m) => m.ZkbioSelfEnrollmentComponent),
  },
  {
    path: 'instalar',
    loadComponent: () =>
      import('./pages/public/install-zivo/install-zivo.component').then(
        (m) => m.InstallZivoComponent,
      ),
  },
  {
    path: 'public/zkbio-face/:token',
    title: 'ZIVO | Registro facial',
    loadComponent: () =>
      import(
        './pages/public/zkbio-face-enrollment/zkbio-face-enrollment.component'
      ).then((m) => m.ZkbioFaceEnrollmentComponent),
  },
  {
    path: 'prospect-external-forms/:token',
    component: PublicProspectoFormComponent,
    title: 'ZIVO ERP | Formulario de postulación',
  },
  {
    path: 'postulaciones/:token',
    component: PublicProspectoFormComponent,
    title: 'ZIVO ERP | Formulario de postulación',
  },
  {
    path: 'postulacion/documentacion/:token',
    canActivate: [AuthGuard],
    title: 'ZIVO ERP | Documentación requerida',
    loadComponent: () =>
      import(
        './pages/postulacion/prospect-document-portal/prospect-document-portal.component'
      ).then((m) => m.ProspectDocumentPortalComponent),
  },
  {
    path: 'restaurante/alimentacion/:accessKey',
    title: 'ZIVO | Portal del restaurante',
    loadComponent: () =>
      import(
        './pages/alimentacion/restaurante-public/restaurant-food-portal.component'
      ).then((m) => m.RestaurantFoodPortalComponent),
  },
  {
    path: 'servicios',
    title: 'ZIVO ERP | Servicios',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/servicios/servicios.module').then(
        (m) => m.ServiciosModule,
      ),
  },
  {
    path: 'ssa',
    title: 'ZIVO ERP | SSA',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/ssa/ssa.module').then((m) => m.SsaModule),
  },
  {
    path: 'servicios-miscelaneos',
    title: 'ZIVO ERP | Servicios misceláneos',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/miscellaneous-services/miscellaneous-services.module').then(
        (m) => m.MiscellaneousServicesModule,
      ),
  },
  {
    path: 'tasklist',
    title: 'ZIVO ERP | Mis Tareas',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/camunda-tasks/camunda-tasks.module').then(
        (m) => m.CamundaTasksModule,
      ),
  },
  {
    path: 'configuraciones',
    title: 'ZIVO ERP | Configuraciones',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/configuraciones/configuraciones.module').then(
        (m) => m.ConfiguracionesModule,
      ),
  },
  {
    path: 'ai',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/kyara-ai/asistants.module').then((m) => m.AsistantModule),
  },
  {
    path: 'productos',
    canActivate: [AuthGuard],
    title: 'ZIVO ERP | Productos',
    loadChildren: () =>
      import('./pages/productos/productos.module').then(
        (m) => m.ProductosModule,
      ),
  },
  {
    path: 'integration-imperium',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/integration-imperium/integration-imperium.module').then(
        (m) => m.IntegrationImperiumModule,
      ),
  },
  {
    path: 'mantenimientos',
    title: 'ZIVO ERP | Mantenimientos',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/mantenimientos/mantenimientos.module').then(
        (m) => m.MantenimientosModule,
      ),
  },
  {
    path: 'notifications',
    title: 'ZIVO ERP | Notificaciones',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/notification-detail/notification.module').then(
        (m) => m.NotificationModule,
      ),
  },
  {
    path: 'fatigue',
    title: 'ZIVO ERP | Fatiga',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/fatigue/fatigue.module').then((m) => m.FatigueModule),
  },
  {
    path: 'alimentacion',
    title: 'ZIVO ERP | Alimentación',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/alimentacion/alimentacion.module').then(
        (m) => m.AlimentacionModule,
      ),
  },
  {
    path: 'project-control',
    title: 'ZIVO ERP | Control de proyectos',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/project-control/project-control.module').then(
        (m) => m.ProjectControlModule,
      ),
  },
  {
    path: 'ite',
    title: 'ZIVO ERP | ITE',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/ite/ite.module').then((m) => m.IteModule),
  },
  {
    path: 'administracion-obras',
    title: 'ZIVO ERP | Administración de Obras',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/administracion-obras/administracion-obras.module').then(
        (m) => m.AdministracionObrasModule,
      ),
  },
  {
    path: 'recursos-humanos',
    title: 'ZIVO ERP | Recursos Humanos',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/recursos-humanos/recursos-humanos.module').then(
        (m) => m.RecursosHumanosModule,
      ),
  },
  {
    path: 'repositorio',
    loadChildren: () =>
      import('./pages/repositorio/repositorio.module').then(
        (m) => m.RepositorioModule,
      ),
  },
  {
    path: 'perfil',
    title: 'ZIVO ERP | Mi perfil',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/perfil/perfil.module').then(
        (m) => m.PerfilModule,
      ),
  },
  {
    path: 'pagos',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/ordenes/payments-requests.module').then(
        (m) => m.PaymentsRequestsModule,
      ),
  },
  {
    path: 'firmas',
    title: 'ZIVO ERP | Firma electrónica',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/electronic-signature/electronic-signature.module').then(
        (m) => m.ElectronicSignatureModule,
      ),
  },
  {
    path: 'formularios',
    title: 'ZIVO ERP | Formularios',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/forms/forms.module').then(
        (m) => m.FormulariosModule,
      ),
  },
  {
    path: 'tasks',
    title: 'ZIVO ERP | Tareas',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/tasks/tasks.module').then((m) => m.TasksModule),
  },
  {
    path: 'teams',
    title: 'ZIVO ERP | Mi equipo',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/my-team/my-team.module').then(
        (m) => m.MyTeamModule,
      ),
  },
  {
    path: 'tickets', canActivate: [AuthGuard, PermisosGuard], data: { permiso: 'tickets/listado' },
    loadChildren: () => import('./pages/tickets/tickets.routes').then((m) => m.TICKET_ROUTES)
  },
  {
    path: 'meetings',
    title: 'ZIVO ERP | Mis reuniones',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/meetings/meetings.module').then(
        (m) => m.MeetingsModule,
      ),
  },
  {
    path: 'public/update-contact-info',
    title: 'ZIVO ERP | Actualizar contacto personal',
    component: UpdateContactInfoComponent,
  },
  {
    path: 'no-autorizado',
    title: 'ZIVO ERP | No autorizado',
    component: NoAutorizadoComponent,
  },
  {
    path: 'mantenimiento',
    component: MantenimientoComponent,
  },
  {
    path: '404',
    title: 'ZIVO ERP | Página no encontrada',
    component: NotFoundComponent,
  },
  { path: '**', redirectTo: '404' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
